import { useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useSearchParams } from "react-router-dom"
import { Box, Fab, Tooltip, Zoom, useTheme } from "@mui/material"
import useScrollTrigger from "@mui/material/useScrollTrigger"

import type { EntityType } from "types/common.types"

import useDateRange from "helpers/hooks/useDateRange"
import useSelectOptions from "helpers/hooks/useSelectOptions"
import { EXIT_ZOOM_BUTTON_BOTTOM } from "helpers/utils/constants"
import { exitZoomMode, formatDateRange } from "widgets/dates/helpers"
import ZoomOutIcon from "icons/ZoomOutIcon"

interface ExitZoomModeButtonProps {
  entity: EntityType
}

const ExitZoomModeButton = ({ entity }: ExitZoomModeButtonProps) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const trigger = useScrollTrigger({
    threshold: 100,
    disableHysteresis: true,
  })
  const { getDateRangeTools } = useDateRange()
  const { dateRange, dispatchDateRange } = useMemo(
    () => getDateRangeTools(entity),
    [getDateRangeTools, entity],
  )
  const [_searchParams, setSearchParams] = useSearchParams()
  const { getDateReferencesByEntity } = useSelectOptions()
  const dateReferences = useMemo(
    () => getDateReferencesByEntity(entity),
    [entity, getDateReferencesByEntity],
  )

  const isZoomActive = dateRange?.zoom ?? false

  const handleExitZoom = useCallback(() => {
    const dateRangeDateTime = formatDateRange(dateRange)
    if (dateRangeDateTime) {
      exitZoomMode(dateRangeDateTime, dateReferences, {
        setSearchParams,
        dispatchDateRange,
      })
    }
  }, [dateRange, dateReferences, setSearchParams, dispatchDateRange])

  // Don't show button if zoom is not active
  if (!isZoomActive) return null

  return (
    <Zoom in={trigger}>
      <Box
        role="presentation"
        sx={{
          position: "fixed",
          bottom: EXIT_ZOOM_BUTTON_BOTTOM,
          right: 32,
          zIndex: 1000,
        }}
      >
        <Tooltip title={t("dates.EXIT_ZOOM_MODE")} placement="left">
          <Fab
            onClick={handleExitZoom}
            color="primary"
            size="medium"
            aria-label="exit-zoom-mode"
            sx={{
              borderRadius: "5px",
              bgcolor: "background.paper",
              border: 1.5,
              borderColor: "primary.main",
            }}
          >
            <ZoomOutIcon color={theme.palette.primary.main} />
          </Fab>
        </Tooltip>
      </Box>
    </Zoom>
  )
}

export default ExitZoomModeButton
